import React, { useState } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PlusIcon, TrashIcon, UserPlusIcon } from '@heroicons/react/24/outline'

const roleOptions = [
  { value: 'admin', label: 'Administrator', description: 'Full access to all features' },
  { value: 'manager', label: 'Manager', description: 'Manage bookings, members, and reports' },
  { value: 'instructor', label: 'Instructor/Coach', description: 'View schedule and manage classes' },
  { value: 'staff', label: 'Staff', description: 'Check-in members and basic operations' },
  { value: 'view_only', label: 'View Only', description: 'Read-only access to reports' }
]

export default function TeamStep() {
  const { wizardData, updateStepData } = useSetupWizard()
  const colors = useThemeColors()
  const { teamMembers } = wizardData
  const [emailError, setEmailError] = useState<string>('')

  const addTeamMember = () => {
    const newMember = {
      id: `member-${Date.now()}`,
      name: '',
      email: '',
      role: 'staff'
    }
    updateStepData('teamMembers', [...teamMembers, newMember])
  }

  const updateTeamMember = (id: string, field: string, value: string) => {
    // Email validation
    if (field === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        setEmailError('Please enter a valid email address')
      } else {
        setEmailError('')
      }
    }

    const updated = teamMembers.map(member =>
      member.id === id ? { ...member, [field]: value } : member
    )
    updateStepData('teamMembers', updated)
  }

  const removeTeamMember = (id: string) => {
    const updated = teamMembers.filter(member => member.id !== id)
    updateStepData('teamMembers', updated)
  }

  const getRoleInfo = (roleValue: string) => {
    return roleOptions.find(role => role.value === roleValue)
  }

  return (
    <div className="team-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Invite your team (optional)
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Add team members who will help manage your facility. They'll receive an invitation email to join.
      </p>

      {/* Skip Note */}
      {teamMembers.length === 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: colors.backgroundLight,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.textSecondary,
            margin: 0
          }}>
            This step is optional. You can invite team members later from your settings.
          </p>
        </div>
      )}

      {/* Role Legend */}
      <div style={{
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <h4 style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: colors.text,
          marginBottom: '0.75rem'
        }}>
          Available Roles:
        </h4>
        <div style={{
          display: 'grid',
          gap: '0.5rem'
        }}>
          {roleOptions.map(role => (
            <div key={role.value} style={{
              display: 'flex',
              gap: '0.5rem',
              fontSize: '0.813rem'
            }}>
              <span style={{ fontWeight: '500', color: colors.text }}>
                {role.label}:
              </span>
              <span style={{ color: colors.textSecondary }}>
                {role.description}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Email Error */}
      {emailError && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: `${colors.error}20`,
          border: `1px solid ${colors.error}40`,
          borderRadius: '6px',
          marginBottom: '1rem',
          fontSize: '0.813rem',
          color: colors.error
        }}>
          {emailError}
        </div>
      )}

      {/* Team Members List */}
      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {teamMembers.map((member, index) => (
          <div
            key={member.id}
            style={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '1.5rem'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <UserPlusIcon style={{ 
                  width: '20px', 
                  height: '20px', 
                  color: colors.primary 
                }} />
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: colors.text,
                  margin: 0
                }}>
                  Team Member {index + 1}
                </h3>
              </div>
              <button
                onClick={() => removeTeamMember(member.id)}
                style={{
                  padding: '0.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.error,
                  cursor: 'pointer'
                }}
              >
                <TrashIcon style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {/* Name and Email */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={member.name}
                    onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.813rem',
                    fontWeight: '500',
                    marginBottom: '0.25rem',
                    color: colors.textSecondary
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={member.email}
                    onChange={(e) => updateTeamMember(member.id, 'email', e.target.value)}
                    placeholder="john@example.com"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      backgroundColor: colors.backgroundLight,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.813rem',
                  fontWeight: '500',
                  marginBottom: '0.25rem',
                  color: colors.textSecondary
                }}>
                  Role
                </label>
                <select
                  value={member.role}
                  onChange={(e) => updateTeamMember(member.id, 'role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    backgroundColor: colors.backgroundLight,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  {roleOptions.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {member.role && (
                  <p style={{
                    fontSize: '0.75rem',
                    color: colors.textMuted,
                    marginTop: '0.25rem',
                    margin: 0
                  }}>
                    {getRoleInfo(member.role)?.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Team Member Button */}
      <button
        onClick={addTeamMember}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: 'transparent',
          border: `2px dashed ${colors.border}`,
          borderRadius: '8px',
          color: colors.primary,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primary
          e.currentTarget.style.backgroundColor = `${colors.primary}10`
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <PlusIcon style={{ width: '20px', height: '20px' }} />
        Add Team Member
      </button>

      {/* Note about invitations */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        backgroundColor: `${colors.primary}10`,
        border: `1px solid ${colors.primary}30`,
        borderRadius: '8px'
      }}>
        <p style={{
          fontSize: '0.813rem',
          color: colors.text,
          margin: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem'
        }}>
          <span>💡</span>
          <span>
            Invitations will be sent after you complete the setup. Team members will need to create their own passwords when they accept the invitation.
          </span>
        </p>
      </div>
    </div>
  )
}